/**
 * portfolioIntelligence.ts — MER Algo Footprints v8.9.1
 *
 * Portfolio Intelligence Layer — hardened.
 *
 * ── Improvements over v8.9 ───────────────────────────────────────────────────
 * 1. Correlation: CorrelationTier type, ETF family mappings, sub-sector precision
 * 2. Exposure: volatility-adjusted exposure field from plan quality + risk metrics
 * 3. Allocation: correlation-concentration factor modulates sizing non-linearly
 * 4. Conflict severity: volatility-aware escalation (high-vol → always 'high')
 * 5. Guidance: portfolio-specific, evidence-based, less generic
 *
 * ── Design rules (unchanged) ─────────────────────────────────────────────────
 * 1. Pure function — same inputs → same outputs, no Date.now() in output
 * 2. No execution, no dollar amounts unless accountSize is explicitly provided
 * 3. All thresholds centralized in PORTFOLIO_THRESHOLDS
 * 4. Explainable — every field carries reason strings
 * 5. Replay-safe — serializable inputs/outputs
 *
 * ── Correlation model (hardened) ─────────────────────────────────────────────
 * CorrelationTier → numeric correlation:
 *   very_high (1.0)  — same symbol
 *   high      (0.75) — same ETF family (both QQQ-components, both SPY, etc.)
 *                     OR same sub-sector (semis trading in same regime)
 *   medium    (0.50) — same broad sector, different sub-sector
 *   low       (0.15) — different sector, same direction (direction beta only)
 *   negative  (−0.40) — opposite direction, same sector (natural hedge)
 *   uncorrelated (0.05) — different sectors, different directions
 *
 * ── Exposure model (hardened) ────────────────────────────────────────────────
 * rawExposure = quality multiplier × base risk unit
 * volatilityAdjustedExposure = rawExposure × volatilityMultiplier
 *   where volatilityMultiplier is derived from plan quality.volatilityFit:
 *     high volfFit  → ×1.0 (plan is calibrated for this vol)
 *     low volFit    → ×0.75 (reduce exposure when vol fit is poor)
 *
 * ── Allocation model (hardened) ──────────────────────────────────────────────
 * Priority score × correlation penalty factor × volatility fit factor
 * Correlation penalty: if plan is in a highly correlated cluster, reduce size
 * Non-linear: first plan in cluster gets full_size, subsequent get reduced
 */

import type { TradePlan, Direction } from '../execution/executionIntelligence';
import type { ExecutionFeedback }    from '../execution/executionFeedback';
import type { RiskModelSettings }    from '../../domains/risk/sizing/positionSizing';

// ── Centralized thresholds ────────────────────────────────────────────────────

export const PORTFOLIO_THRESHOLDS = {
  // Max directional exposure (% of total risk units) before flagging
  MAX_DIRECTIONAL_PCT:      0.65,

  // Max single-plan exposure before flagging
  MAX_SINGLE_PLAN_PCT:      0.40,

  // Plan quality floor for portfolio inclusion
  MIN_PLAN_QUALITY:         0.55,

  // ── Correlation tier numeric values ──────────────────────────────────────
  CORR_VERY_HIGH:           0.90,   // same symbol or ETF twins
  CORR_HIGH:                0.70,   // same sub-sector or ETF family
  CORR_MEDIUM:              0.45,   // same broad sector
  CORR_LOW:                 0.15,   // same direction only
  CORR_NEGATIVE_SECTOR:    -0.40,   // opposite direction, same sector
  CORR_UNCORRELATED:        0.05,

  // ── Conflict trigger thresholds ───────────────────────────────────────────
  CORR_CONFLICT_TRIGGER:    0.65,   // ≥0.65 same-direction = hidden concentration
  CORR_HEDGE_TRIGGER:      -0.30,   // ≤−0.30 = intentional hedge zone

  // Risk unit quality multipliers
  RISK_UNIT_HIGH:           1.20,
  RISK_UNIT_MODERATE:       1.00,
  RISK_UNIT_LOW:            0.60,

  // Volatility fit multipliers for exposure adjustment
  VOL_FIT_HIGH_THRESHOLD:   0.70,   // volFit ≥ 0.70 → full exposure
  VOL_FIT_LOW_THRESHOLD:    0.45,   // volFit < 0.45 → reduced exposure
  VOL_EXPOSURE_REDUCTION:   0.75,   // multiply exposure by this when vol fit is poor

  // Correlation cluster penalty (for allocation guidance)
  CORR_CLUSTER_SECOND_PCT:  0.50,   // 2nd plan in high-corr cluster → half_size
  CORR_CLUSTER_THIRD_PCT:   0.00,   // 3rd+ plan in cluster → avoid

  // Symbol group mappings — deterministic, rule-based
  // Sub-sector groups (highest precision)
  SEMI_SYMBOLS:    ['NVDA','AMD','INTC','TSM','QCOM','AVGO','AMAT','MU','LRCX','KLAC','ON','ASML','MRVL'] as string[],
  MEGA_CAP_TECH:   ['AAPL','MSFT','GOOGL','GOOG','META','AMZN','TSLA','NVDA'] as string[],
  MID_TECH:        ['CRM','NOW','ADBE','ORCL','IBM','CSCO','PANW','CRWD','ZS','SNOW'] as string[],
  // ETF families (treat members as highly correlated)
  QQQ_FAMILY:      ['QQQ','TQQQ','SQQQ','QLD'] as string[],
  SPY_FAMILY:      ['SPY','SPXL','SPXS','SSO','SDS','VOO','IVV'] as string[],
  FINANCE_SYMBOLS: ['JPM','GS','BAC','WFC','MS','C','BLK','SCHW','AXP','V','MA'] as string[],
  ENERGY_SYMBOLS:  ['XOM','CVX','COP','OXY','SLB','HAL','MPC','PSX','PXD','EOG'] as string[],
  BIOTECH_SYMBOLS: ['BIIB','GILD','AMGN','MRNA','BNTX','REGN','VRTX','ABBV'] as string[],
} as const;

const T = PORTFOLIO_THRESHOLDS;

// ── Types ─────────────────────────────────────────────────────────────────────

export type CorrelationTier = 'very_high' | 'high' | 'medium' | 'low' | 'negative' | 'uncorrelated';
export type DirectionalBias = 'long' | 'short' | 'neutral' | 'mixed';
export type ConcentrationLevel = 'safe' | 'elevated' | 'high' | 'critical';
export type ConflictType = 'directional' | 'correlation' | 'overexposure' | 'volatility_instability';

export interface PlanExposure {
  symbol:          string;
  direction:       Direction;
  /** Raw risk units from quality tier */
  riskUnits:       number;
  /** Volatility-adjusted risk units (reduced when vol fit is poor) */
  adjustedRiskUnits: number;
  /** Dollar risk estimate — null when accountSize is not configured */
  dollarRisk:      number | null;
  /** Fraction of total portfolio risk (using adjustedRiskUnits) */
  portfolioWeight: number;
  qualityTier:     string;
  /** Vol fit score from plan quality (0–1) */
  volFit:          number;
  planSummary:     string;
}

export interface CorrelationPair {
  symbolA:     string;
  symbolB:     string;
  correlation: number;
  tier:        CorrelationTier;
  type:        'positive' | 'negative' | 'neutral';
  reason:      string;
}

export interface ConflictSignal {
  type:        ConflictType;
  severity:    'low' | 'medium' | 'high';
  symbols:     string[];
  description: string;
  remedy:      string;
}

export interface PortfolioState {
  planCount:           number;
  activePlans:         PlanExposure[];
  totalRiskUnits:      number;
  totalAdjustedRisk:   number;
  dollarRiskTotal:     number | null;
  directionalBias:     DirectionalBias;
  longRiskPct:         number;
  shortRiskPct:        number;
  concentrationRisk:   ConcentrationLevel;
  topConcentration:    { symbol: string; pct: number } | null;
}

export interface PrioritizedPlan {
  rank:               number;
  plan:               TradePlan;
  priorityScore:      number;
  rationale:          string;
  deprioritized:      boolean;
  deprioritizeReason: string | null;
  /** Cluster position — how many high-corr plans precede this one */
  correlationClusterRank: number;
}

export interface AllocationGuidance {
  symbol:          string;
  action:          'full_size' | 'half_size' | 'avoid' | 'reduce';
  riskUnitTarget:  number;
  reason:          string;
}

export interface PortfolioIntelligenceOutput {
  state:        PortfolioState;
  correlations: CorrelationPair[];
  conflicts:    ConflictSignal[];
  prioritized:  PrioritizedPlan[];
  allocation:   AllocationGuidance[];
  guidance:     string[];
  reasoning:    string[];
}

// ── Step 1: Hardened correlation model ────────────────────────────────────────

type SymbolGroup = {
  name:    string;
  tier:    'very_high' | 'high' | 'medium';
  symbols: readonly string[];
};

const SYMBOL_GROUPS: SymbolGroup[] = [
  { name: 'QQQ ETF family',    tier: 'very_high', symbols: T.QQQ_FAMILY    },
  { name: 'SPY ETF family',    tier: 'very_high', symbols: T.SPY_FAMILY    },
  { name: 'Semiconductors',    tier: 'high',      symbols: T.SEMI_SYMBOLS  },
  { name: 'Mega-cap tech',     tier: 'high',      symbols: T.MEGA_CAP_TECH },
  { name: 'Mid-cap tech/SaaS', tier: 'medium',    symbols: T.MID_TECH      },
  { name: 'Financials',        tier: 'medium',    symbols: T.FINANCE_SYMBOLS},
  { name: 'Energy',            tier: 'medium',    symbols: T.ENERGY_SYMBOLS },
  { name: 'Biotech',           tier: 'medium',    symbols: T.BIOTECH_SYMBOLS},
];

function _groupOf(symbol: string): { name: string; tier: 'very_high' | 'high' | 'medium' } | null {
  const s = symbol.toUpperCase();
  // Return the highest-tier group first (iteration order = priority)
  for (const g of SYMBOL_GROUPS) {
    if ((g.symbols as string[]).includes(s)) return g;
  }
  return null;
}

function _corrTierToNum(tier: CorrelationTier): number {
  switch (tier) {
    case 'very_high':   return T.CORR_VERY_HIGH;
    case 'high':        return T.CORR_HIGH;
    case 'medium':      return T.CORR_MEDIUM;
    case 'low':         return T.CORR_LOW;
    case 'negative':    return T.CORR_NEGATIVE_SECTOR;
    case 'uncorrelated':return T.CORR_UNCORRELATED;
  }
}

function _correlation(planA: TradePlan, planB: TradePlan): CorrelationPair {
  const sA = planA.symbol.toUpperCase();
  const sB = planB.symbol.toUpperCase();
  const sameDir = planA.direction === planB.direction;

  // Same symbol
  if (sA === sB) {
    return { symbolA: sA, symbolB: sB, correlation: 1.0, tier: 'very_high', type: 'positive', reason: 'Same symbol' };
  }

  const groupA = _groupOf(sA);
  const groupB = _groupOf(sB);

  // Both in same group
  if (groupA && groupB && groupA.name === groupB.name) {
    const baseTier = groupA.tier as CorrelationTier;
    if (sameDir) {
      const num = _corrTierToNum(baseTier);
      return { symbolA: sA, symbolB: sB, correlation: num, tier: baseTier, type: 'positive', reason: `Same group: ${groupA.name}` };
    } else {
      // Opposite direction in same group → negative correlation (natural hedge)
      const num = T.CORR_NEGATIVE_SECTOR;
      return { symbolA: sA, symbolB: sB, correlation: num, tier: 'negative', type: 'negative', reason: `Opposite directions within ${groupA.name} — partial hedge` };
    }
  }

  // Both have groups, different groups — check for group tier relationship
  if (groupA && groupB) {
    // Both tech-broad (mega + semi + mid) → medium correlation
    const bothTech = ['Semiconductors','Mega-cap tech','Mid-cap tech/SaaS'].includes(groupA.name) &&
                     ['Semiconductors','Mega-cap tech','Mid-cap tech/SaaS'].includes(groupB.name);
    if (bothTech && sameDir) {
      return { symbolA: sA, symbolB: sB, correlation: T.CORR_MEDIUM, tier: 'medium', type: 'positive', reason: `Cross-tech sector (${groupA.name} + ${groupB.name})` };
    }
    if (bothTech && !sameDir) {
      return { symbolA: sA, symbolB: sB, correlation: T.CORR_NEGATIVE_SECTOR * 0.7, tier: 'negative', type: 'negative', reason: `Cross-tech, opposite directions` };
    }
    // Different non-tech sectors, same direction
    if (sameDir) {
      return { symbolA: sA, symbolB: sB, correlation: T.CORR_LOW, tier: 'low', type: 'positive', reason: `Different sectors, same direction (direction beta)` };
    }
    return { symbolA: sA, symbolB: sB, correlation: T.CORR_UNCORRELATED, tier: 'uncorrelated', type: 'neutral', reason: `Different sectors, opposite directions` };
  }

  // At least one unknown group
  if (sameDir) {
    return { symbolA: sA, symbolB: sB, correlation: T.CORR_LOW, tier: 'low', type: 'positive', reason: 'Same direction only' };
  }
  return { symbolA: sA, symbolB: sB, correlation: T.CORR_UNCORRELATED, tier: 'uncorrelated', type: 'neutral', reason: 'Unrelated — different directions' };
}

function _buildCorrelations(plans: TradePlan[]): CorrelationPair[] {
  const pairs: CorrelationPair[] = [];
  for (let i = 0; i < plans.length; i++) {
    for (let j = i + 1; j < plans.length; j++) {
      pairs.push(_correlation(plans[i], plans[j]));
    }
  }
  return pairs.sort((a, b) => Math.abs(b.correlation) - Math.abs(a.correlation));
}

// ── Step 2: Hardened exposure model ───────────────────────────────────────────

function _riskUnitMultiplier(tier: string): number {
  if (tier === 'high')     return T.RISK_UNIT_HIGH;
  if (tier === 'moderate') return T.RISK_UNIT_MODERATE;
  return T.RISK_UNIT_LOW;
}

/** Volatility-based exposure multiplier — reduces size when vol fit is poor */
function _volExposureMultiplier(volFit: number): number {
  if (volFit >= T.VOL_FIT_HIGH_THRESHOLD) return 1.0;
  if (volFit < T.VOL_FIT_LOW_THRESHOLD)   return T.VOL_EXPOSURE_REDUCTION;
  // Linear interpolation in the middle range
  const range = T.VOL_FIT_HIGH_THRESHOLD - T.VOL_FIT_LOW_THRESHOLD;
  const pos   = (volFit - T.VOL_FIT_LOW_THRESHOLD) / range;
  return T.VOL_EXPOSURE_REDUCTION + pos * (1.0 - T.VOL_EXPOSURE_REDUCTION);
}

function _buildExposures(
  plans:     TradePlan[],
  riskModel: RiskModelSettings,
): PlanExposure[] {
  const rawUnits   = plans.map(p => _riskUnitMultiplier(p.quality.tier));
  const volFits    = plans.map(p => p.quality.volatilityFit);
  const adjUnits   = plans.map((_, i) => rawUnits[i] * _volExposureMultiplier(volFits[i]));
  const adjTotal   = adjUnits.reduce((s, v) => s + v, 0) || 1;

  return plans.map((p, i) => {
    const riskUnits         = Math.round(rawUnits[i] * 100) / 100;
    const adjustedRiskUnits = Math.round(adjUnits[i] * 100) / 100;
    const portfolioWeight   = Math.round((adjUnits[i] / adjTotal) * 1000) / 1000;
    const dollarRisk        = riskModel.accountSize !== null
      ? Math.round(riskModel.accountSize * (riskModel.riskPerTradePct / 100) * adjustedRiskUnits * 100) / 100
      : null;
    return {
      symbol: p.symbol, direction: p.direction,
      riskUnits, adjustedRiskUnits, dollarRisk,
      portfolioWeight, qualityTier: p.quality.tier,
      volFit: Math.round(volFits[i] * 100) / 100,
      planSummary: p.summary,
    };
  });
}

// ── Step 3: Portfolio state ───────────────────────────────────────────────────

function _buildPortfolioState(
  plans:     TradePlan[],
  exposures: PlanExposure[],
): PortfolioState {
  const totalRiskUnits   = exposures.reduce((s, e) => s + e.riskUnits, 0);
  const totalAdjustedRisk = exposures.reduce((s, e) => s + e.adjustedRiskUnits, 0);
  const dollarRiskTotal  = exposures.some(e => e.dollarRisk !== null)
    ? exposures.reduce((s, e) => s + (e.dollarRisk ?? 0), 0) : null;

  const longAdj  = exposures.filter(e => e.direction === 'long').reduce((s, e) => s + e.adjustedRiskUnits, 0);
  const shortAdj = exposures.filter(e => e.direction === 'short').reduce((s, e) => s + e.adjustedRiskUnits, 0);
  const longPct  = totalAdjustedRisk > 0 ? Math.round(longAdj  / totalAdjustedRisk * 100) : 0;
  const shortPct = totalAdjustedRisk > 0 ? Math.round(shortAdj / totalAdjustedRisk * 100) : 0;

  const directionalBias: DirectionalBias =
    longPct  > 65 ? 'long'    :
    shortPct > 65 ? 'short'   :
    longPct === shortPct ? 'neutral' : 'mixed';

  const maxExposure = exposures.reduce(
    (max, e) => e.portfolioWeight > max.pct ? { symbol: e.symbol, pct: e.portfolioWeight } : max,
    { symbol: '', pct: 0 }
  );
  const concentrationRisk: ConcentrationLevel =
    maxExposure.pct >= 0.70 ? 'critical' :
    maxExposure.pct >= 0.50 ? 'high'     :
    maxExposure.pct >= 0.35 ? 'elevated' : 'safe';

  return {
    planCount: plans.length,
    activePlans: exposures,
    totalRiskUnits: Math.round(totalRiskUnits * 100) / 100,
    totalAdjustedRisk: Math.round(totalAdjustedRisk * 100) / 100,
    dollarRiskTotal: dollarRiskTotal !== null ? Math.round(dollarRiskTotal * 100) / 100 : null,
    directionalBias, longRiskPct: longPct, shortRiskPct: shortPct,
    concentrationRisk,
    topConcentration: maxExposure.symbol ? { symbol: maxExposure.symbol, pct: Math.round(maxExposure.pct * 100) } : null,
  };
}

// ── Step 4: Hardened conflict detection ───────────────────────────────────────

function _isHighVol(plans: TradePlan[], symbols: string[]): boolean {
  return plans
    .filter(p => symbols.includes(p.symbol))
    .some(p => p.quality.volatilityFit < T.VOL_FIT_LOW_THRESHOLD);
}

function _detectConflicts(
  state:        PortfolioState,
  correlations: CorrelationPair[],
  plans:        TradePlan[],
): ConflictSignal[] {
  const conflicts: ConflictSignal[] = [];

  // Directional overexposure
  if (state.longRiskPct > T.MAX_DIRECTIONAL_PCT * 100) {
    const longSymbols = state.activePlans.filter(e => e.direction === 'long').map(e => e.symbol);
    const highVol = _isHighVol(plans, longSymbols);
    conflicts.push({
      type: 'directional',
      severity: state.longRiskPct > 80 || highVol ? 'high' : 'medium',
      symbols: longSymbols,
      description: `${state.longRiskPct}% of adjusted risk in LONG plans${highVol ? ' — elevated under poor vol fit' : ''}`,
      remedy: 'Reduce long exposure on the lowest-quality plan, or add a short-biased hedge',
    });
  }
  if (state.shortRiskPct > T.MAX_DIRECTIONAL_PCT * 100) {
    const shortSymbols = state.activePlans.filter(e => e.direction === 'short').map(e => e.symbol);
    const highVol = _isHighVol(plans, shortSymbols);
    conflicts.push({
      type: 'directional',
      severity: state.shortRiskPct > 80 || highVol ? 'high' : 'medium',
      symbols: shortSymbols,
      description: `${state.shortRiskPct}% of adjusted risk in SHORT plans${highVol ? ' — squeeze risk elevated' : ''}`,
      remedy: 'Trim weakest short setup or add long-biased hedge',
    });
  }

  // Single-plan overexposure
  if (state.topConcentration && state.topConcentration.pct > T.MAX_SINGLE_PLAN_PCT * 100) {
    conflicts.push({
      type: 'overexposure',
      severity: state.topConcentration.pct > 60 ? 'high' : 'medium',
      symbols: [state.topConcentration.symbol],
      description: `${state.topConcentration.symbol} is ${state.topConcentration.pct}% of vol-adjusted portfolio risk`,
      remedy: `Scale back ${state.topConcentration.symbol} or add uncorrelated setups to rebalance`,
    });
  }

  // Correlation conflicts — hardened with tier awareness
  for (const pair of correlations) {
    const plansInPair = plans.filter(p => p.symbol === pair.symbolA || p.symbol === pair.symbolB);
    if (plansInPair.length < 2) continue;

    // Hidden concentration: high/very_high correlation, same direction
    if (pair.correlation >= T.CORR_CONFLICT_TRIGGER) {
      const sameDir = plansInPair[0].direction === plansInPair[1].direction;
      if (sameDir) {
        const highVol = _isHighVol(plans, [pair.symbolA, pair.symbolB]);
        const severity = pair.tier === 'very_high' || highVol ? 'high' : 'medium';
        conflicts.push({
          type: 'correlation', severity,
          symbols: [pair.symbolA, pair.symbolB],
          description: `${pair.symbolA} + ${pair.symbolB}: ${pair.tier.replace('_',' ')} correlation (${pair.correlation.toFixed(2)}), both ${plansInPair[0].direction} — ${pair.reason}`,
          remedy: `Treat as one position for sizing. In high-vol conditions, reduce the lower-quality plan.`,
        });
      }
    }

    // Hedge detection: very negative correlation, opposite directions
    if (pair.correlation <= T.CORR_HEDGE_TRIGGER && plansInPair[0].direction !== plansInPair[1].direction) {
      conflicts.push({
        type: 'correlation', severity: 'low',
        symbols: [pair.symbolA, pair.symbolB],
        description: `${pair.symbolA} (${plansInPair[0].direction}) vs ${pair.symbolB} (${plansInPair[1].direction}): ${pair.reason} — verify intentional hedge`,
        remedy: 'If intentional hedge: accept. If not: remove the weaker-quality side.',
      });
    }

    // Volatility instability: poor vol fit across correlated plans
    if (pair.correlation >= T.CORR_MEDIUM && _isHighVol(plans, [pair.symbolA, pair.symbolB])) {
      conflicts.push({
        type: 'volatility_instability', severity: 'medium',
        symbols: [pair.symbolA, pair.symbolB],
        description: `${pair.symbolA} + ${pair.symbolB} are correlated (${pair.reason}) and both have poor vol-fit — exposure is optically larger than it appears`,
        remedy: 'Reduce position size on the lower-priority plan until vol regime normalizes',
      });
    }
  }

  // Deduplicate by (type + sorted symbols)
  const seen = new Set<string>();
  const deduped = conflicts.filter(c => {
    const key = `${c.type}:${[...c.symbols].sort().join(',')}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return deduped.sort((a, b) => {
    const ord: Record<string, number> = { high: 0, medium: 1, low: 2 };
    return ord[a.severity] - ord[b.severity];
  });
}

// ── Step 5: Plan prioritization with correlation cluster tracking ──────────────

function _buildCorrelationClusterRank(
  plan:         TradePlan,
  prioritized:  Array<{ plan: TradePlan; priorityScore: number }>,
  correlations: CorrelationPair[],
): number {
  // How many higher-ranked plans are highly correlated with this one?
  let rank = 0;
  for (const higher of prioritized) {
    const pair = correlations.find(c =>
      (c.symbolA === plan.symbol && c.symbolB === higher.plan.symbol) ||
      (c.symbolB === plan.symbol && c.symbolA === higher.plan.symbol)
    );
    if (pair && pair.correlation >= T.CORR_CONFLICT_TRIGGER && plan.direction === higher.plan.direction) {
      rank++;
    }
  }
  return rank;
}

function _prioritizePlans(
  plans:        TradePlan[],
  exposures:    PlanExposure[],
  feedbacks:    ExecutionFeedback[],
  conflicts:    ConflictSignal[],
  correlations: CorrelationPair[],
): PrioritizedPlan[] {
  const feedbackMap = new Map(feedbacks.map(f => [f.planId.split(':')[0], f]));

  const scored = plans.map(plan => {
    // Base: plan quality (0–60)
    let score = plan.quality.overall * 60;

    // Execution history (0–25)
    const fb = feedbackMap.get(plan.symbol);
    score += fb ? (fb.score.overall / 100) * 25 : 12;

    // Confluence strength (0–10)
    score += Math.min(10, plan.quality.confluenceScore * 10);

    // Vol fit bonus/penalty (0–5 bonus / up to −5 penalty)
    const volFit = plan.quality.volatilityFit;
    score += (volFit - 0.5) * 10;   // centered at 0.5: ×10 → ±5

    // High-severity conflict penalty
    const inHighConflict = conflicts.some(c => c.severity === 'high' && c.symbols.includes(plan.symbol));
    let deprioritized = false;
    let deprioritizeReason: string | null = null;
    if (inHighConflict) {
      score *= 0.70;
      deprioritized = true;
      deprioritizeReason = 'Involved in high-severity portfolio conflict — see Conflicts tab';
    }

    const totalScore = Math.min(100, Math.round(score));
    const exp = exposures.find(e => e.symbol === plan.symbol);
    const rationale = [
      `Quality: ${plan.quality.tier} (${(plan.quality.overall * 100).toFixed(0)}%)`,
      fb ? `Exec score: ${fb.score.overall}/100` : 'No exec history',
      `Vol fit: ${(volFit * 100).toFixed(0)}%`,
      `Weight: ${exp ? (exp.portfolioWeight * 100).toFixed(0) : '—'}%`,
    ].join(' · ');

    return { plan, priorityScore: totalScore, rationale, deprioritized, deprioritizeReason };
  });

  // Sort: non-deprioritized first, then score descending
  scored.sort((a, b) => {
    if (a.deprioritized !== b.deprioritized) return a.deprioritized ? 1 : -1;
    return b.priorityScore - a.priorityScore;
  });

  // Add cluster rank for each plan
  const result: PrioritizedPlan[] = [];
  for (const s of scored) {
    const clusterRank = _buildCorrelationClusterRank(s.plan, result, correlations);
    result.push({ rank: result.length + 1, ...s, correlationClusterRank: clusterRank });
  }
  return result;
}

// ── Step 6: Hardened allocation guidance with correlation-cluster sizing ───────

function _buildAllocation(
  prioritized:  PrioritizedPlan[],
  state:        PortfolioState,
  riskModel:    RiskModelSettings,
): AllocationGuidance[] {
  const maxPlans = riskModel.maxConcurrentTrades;

  return prioritized.map(({ rank, plan, deprioritized, deprioritizeReason, correlationClusterRank }) => {
    let action: AllocationGuidance['action'];
    let riskUnitTarget: number;
    let reason: string;

    // Gate 1: exceeds max concurrent
    if (rank > maxPlans) {
      return { symbol: plan.symbol, action: 'avoid', riskUnitTarget: 0,
        reason: `Rank ${rank} exceeds max concurrent trades (${maxPlans})` };
    }

    // Gate 2: deprioritized by conflict
    if (deprioritized) {
      return { symbol: plan.symbol, action: 'half_size', riskUnitTarget: 0.5,
        reason: deprioritizeReason ?? 'Deprioritized due to conflict' };
    }

    // Gate 3: correlation cluster penalty (non-linear)
    if (correlationClusterRank >= 2) {
      return { symbol: plan.symbol, action: 'avoid', riskUnitTarget: 0,
        reason: `3rd+ plan in high-correlation cluster — hidden concentration risk` };
    }
    if (correlationClusterRank === 1) {
      action = 'half_size';
      riskUnitTarget = T.CORR_CLUSTER_SECOND_PCT;
      reason = `2nd plan in high-correlation cluster — reduce size to avoid hidden doubling`;
    }
    // Gate 4: concentration-aware sizing
    else if (state.concentrationRisk === 'critical' || state.concentrationRisk === 'high') {
      action = rank === 1 ? 'full_size' : 'half_size';
      riskUnitTarget = rank === 1 ? 1.0 : 0.5;
      reason = rank === 1
        ? `Top-ranked plan — full size despite portfolio concentration`
        : `Portfolio concentration is ${state.concentrationRisk} — reduce all non-top setups`;
    }
    // Gate 5: directional concentration
    else if (state.directionalBias !== 'neutral' && state.directionalBias !== 'mixed') {
      const inBiasDir = plan.direction === state.directionalBias;
      action = inBiasDir ? 'half_size' : 'full_size';
      riskUnitTarget = inBiasDir ? 0.5 : 1.0;
      reason = inBiasDir
        ? `Adds to ${state.directionalBias}-heavy portfolio — reduce to avoid directional stack`
        : `Counter-directional to portfolio bias — provides balance, full size`;
    }
    // Gate 6: vol-fit based reduction
    else {
      const exp = state.activePlans.find(e => e.symbol === plan.symbol);
      if (exp && exp.volFit < T.VOL_FIT_LOW_THRESHOLD) {
        action = 'half_size';
        riskUnitTarget = 0.5;
        reason = `Vol fit ${(exp.volFit * 100).toFixed(0)}% is poor — reduce size until regime clarifies`;
      } else {
        action = 'full_size';
        riskUnitTarget = 1.0;
        reason = 'No portfolio constraints — standard risk unit';
      }
    }

    return { symbol: plan.symbol, action, riskUnitTarget, reason };
  });
}

// ── Step 7: Portfolio guidance ────────────────────────────────────────────────

function _buildGuidance(
  state:       PortfolioState,
  conflicts:   ConflictSignal[],
  prioritized: PrioritizedPlan[],
): string[] {
  const guidance: string[] = [];

  // Concentration
  if (state.concentrationRisk === 'critical') {
    guidance.push(`⊗ Critical: ${state.topConcentration?.symbol} is ${state.topConcentration?.pct}% of vol-adjusted exposure — reduce before adding any new positions`);
  } else if (state.concentrationRisk === 'high') {
    guidance.push(`⚠ High concentration: ${state.topConcentration?.symbol} at ${state.topConcentration?.pct}% — scale back before stacking additional risk`);
  }

  // Directional
  if (state.directionalBias === 'long' && state.longRiskPct > 70) {
    const correlated = conflicts.filter(c => c.type === 'correlation' && c.severity !== 'low').length;
    guidance.push(`${state.longRiskPct}% long exposure${correlated > 0 ? ` with ${correlated} correlated plan pair${correlated > 1 ? 's' : ''}` : ''} — a broad market sell-off affects all positions simultaneously`);
  }
  if (state.directionalBias === 'short' && state.shortRiskPct > 70) {
    guidance.push(`${state.shortRiskPct}% short exposure — systematic risk-on squeeze could impact all short positions; verify each has independent invalidation logic`);
  }

  // High-severity conflicts
  const highConflicts = conflicts.filter(c => c.severity === 'high');
  if (highConflicts.length > 0) {
    const types = [...new Set(highConflicts.map(c => c.type))].join(', ');
    guidance.push(`${highConflicts.length} high-severity conflict${highConflicts.length > 1 ? 's' : ''} (${types}) — resolve before executing any allocation guidance`);
  }

  // Correlated clusters
  const clustered = prioritized.filter(p => p.correlationClusterRank > 0);
  if (clustered.length > 0) {
    guidance.push(`${clustered.length} plan${clustered.length > 1 ? 's are' : ' is'} in high-correlation clusters with higher-ranked plans — size reduced in allocation guidance to prevent hidden doubling`);
  }

  // Vol fit warning
  const poorVol = state.activePlans.filter(e => e.volFit < T.VOL_FIT_LOW_THRESHOLD);
  if (poorVol.length > 0) {
    guidance.push(`${poorVol.length} plan${poorVol.length > 1 ? 's have' : ' has'} poor volatility fit (${poorVol.map(e => e.symbol).join(', ')}) — vol-adjusted exposure is reduced; conditions may not favor these setups`);
  }

  // Positive guidance
  if (state.concentrationRisk === 'safe' && highConflicts.length === 0 && clustered.length === 0) {
    guidance.push(`✓ Portfolio is well-structured — no conflicts, concentration, or correlation stacking detected. Proceed with allocation guidance.`);
  }
  if ((state.directionalBias === 'mixed' || state.directionalBias === 'neutral') && state.planCount >= 2) {
    guidance.push(`✓ Directional exposure is balanced (${state.longRiskPct}% long / ${state.shortRiskPct}% short) — market-directional risk is distributed.`);
  }

  if (guidance.length === 0) {
    guidance.push('Add watchlist symbols and run analysis to generate portfolio-specific guidance.');
  }

  return guidance;
}

// ── Main export ───────────────────────────────────────────────────────────────

/**
 * Build portfolio intelligence from active trade plans.
 * PURE FUNCTION — deterministic, no I/O, no Date.now() in output.
 */
export function buildPortfolioIntelligence(
  plans:     TradePlan[],
  feedbacks: ExecutionFeedback[],
  riskModel: RiskModelSettings,
): PortfolioIntelligenceOutput {
  if (plans.length === 0) {
    return {
      state: {
        planCount: 0, activePlans: [], totalRiskUnits: 0, totalAdjustedRisk: 0,
        dollarRiskTotal: null, directionalBias: 'neutral', longRiskPct: 0, shortRiskPct: 0,
        concentrationRisk: 'safe', topConcentration: null,
      },
      correlations: [], conflicts: [], prioritized: [], allocation: [],
      guidance: ['No active trade plans. Run analysis on watchlist symbols.'],
      reasoning: ['No plans provided'],
    };
  }

  // Deterministic sort
  const sortedPlans = [...plans].sort((a, b) => a.symbol.localeCompare(b.symbol));

  const exposures    = _buildExposures(sortedPlans, riskModel);
  const state        = _buildPortfolioState(sortedPlans, exposures);
  const correlations = _buildCorrelations(sortedPlans);
  const conflicts    = _detectConflicts(state, correlations, sortedPlans);
  const prioritized  = _prioritizePlans(sortedPlans, exposures, feedbacks, conflicts, correlations);
  const allocation   = _buildAllocation(prioritized, state, riskModel);
  const guidance     = _buildGuidance(state, conflicts, prioritized);

  const reasoning: string[] = [
    `Plans: ${plans.length} | Raw risk: ${state.totalRiskUnits} | Adj risk: ${state.totalAdjustedRisk}`,
    `Direction: ${state.directionalBias} (${state.longRiskPct}% long / ${state.shortRiskPct}% short)`,
    `Concentration: ${state.concentrationRisk}${state.topConcentration ? ` — ${state.topConcentration.symbol} (${state.topConcentration.pct}%)` : ''}`,
    `Conflicts: ${conflicts.length} | Correlation pairs: ${correlations.length}`,
    `Priority: ${prioritized.map(p => `${p.plan.symbol}(${p.priorityScore})`).join(' → ')}`,
  ];

  return { state, correlations, conflicts, prioritized, allocation, guidance, reasoning };
}
